import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type { CSSProperties, FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  AssistantPromptSelection,
  PromptLoadState,
  ServiceReadiness,
} from 'contexts/llmProxySerivceContext';
import type { GQLPrompt } from 'graphql/questions-and-answers/queries';
import { PromptChangeConfirmModal } from './prompt-change-confirm-modal';

export type PromptSelectorProps = {
  readonly prompts: readonly GQLPrompt[];
  readonly promptSelection: AssistantPromptSelection;
  readonly selectPrompt: (prompt: GQLPrompt | null) => void;
  readonly triggerClearConversation: () => void;
  readonly hasMessages: boolean;
  readonly promptLoadState: PromptLoadState;
  readonly serviceReadiness: ServiceReadiness;
  readonly disabled?: boolean;
};

type StatusArgs = {
  readonly prompts: readonly GQLPrompt[];
  readonly promptLoadState: PromptLoadState;
  readonly serviceReadiness: ServiceReadiness;
};

type StatusMessageKey =
  | 'questionsAndAnswers:promptLoading'
  | 'questionsAndAnswers:promptEmpty'
  | 'questionsAndAnswers:promptUnavailable'
  | 'questionsAndAnswers:promptInitializationError';

const assertNever = (value: never): never => {
  throw new Error(`Unhandled prompt selector state: ${String(value)}`);
};

const getStatusMessageKey = ({
  prompts,
  promptLoadState,
  serviceReadiness,
}: StatusArgs): StatusMessageKey | null => {
  switch (serviceReadiness.status) {
    case 'error':
      return 'questionsAndAnswers:promptInitializationError';
    case 'initializing':
      return 'questionsAndAnswers:promptLoading';
    case 'ready':
      break;
    default:
      return assertNever(serviceReadiness);
  }

  switch (promptLoadState.status) {
    case 'loading':
      return 'questionsAndAnswers:promptLoading';
    case 'error':
      return 'questionsAndAnswers:promptUnavailable';
    case 'ready':
      return prompts.length === 0 ? 'questionsAndAnswers:promptEmpty' : null;
    default:
      return assertNever(promptLoadState);
  }
};

const getDescriptionSummary = (description?: string | null): string => {
  const trimmed = description?.trim() ?? '';
  const firstPeriod = trimmed.indexOf('.');
  return firstPeriod === -1 ? trimmed : trimmed.slice(0, firstPeriod + 1);
};

export const PromptSelector: FC<PromptSelectorProps & { textColor?: CSSProperties['color'] }> = ({
  prompts,
  promptSelection,
  selectPrompt,
  triggerClearConversation,
  hasMessages,
  promptLoadState,
  serviceReadiness,
  textColor,
  disabled: disabledProp,
}) => {
  const { t } = useTranslation();
  const statusMessageKey = getStatusMessageKey({ prompts, promptLoadState, serviceReadiness });
  const selectedPromptId = promptSelection.kind === 'custom'
    && prompts.some((prompt) => prompt._id === promptSelection.prompt._id)
    ? promptSelection.prompt._id
    : '';
  const readinessDisabled = serviceReadiness.status !== 'ready' || promptLoadState.status !== 'ready';
  const isDisabled = readinessDisabled || disabledProp === true;
  const [pendingPromptId, setPendingPromptId] = useState<string | null>(null);

  const pendingPrompt = pendingPromptId
    ? prompts.find((prompt) => prompt._id === pendingPromptId)
    : undefined;
  const pendingPromptDescription = pendingPrompt?.english?.description?.trim() ?? '';

  const handleConfirm = () => {
    if (pendingPromptId === null) return;
    const selectedPrompt = prompts.find((prompt) => prompt._id === pendingPromptId);
    selectPrompt(selectedPrompt ?? null);
    triggerClearConversation();
    setPendingPromptId(null);
  };

  const handleClose = () => {
    setPendingPromptId(null);
  };

  return (
    <>
      <FormControl fullWidth disabled={isDisabled} style={{ color: textColor }}>
        <InputLabel
          id="assistant-prompt-selector-label"
          shrink
          style={{ color: textColor, fontSize: '0.75rem' }}
        >
          {t('questionsAndAnswers:promptSelector')}
        </InputLabel>
        <Select
          labelId="assistant-prompt-selector-label"
          value={selectedPromptId}
          displayEmpty
          onChange={(event) => {
            const promptId = String(event.target.value);
            if (promptId === selectedPromptId) return;
            if (promptId === '' && !hasMessages) {
              selectPrompt(null);
              triggerClearConversation();
              return;
            }
            setPendingPromptId(promptId);
          }}
          renderValue={(value) => {
            if (value === '') return t('questionsAndAnswers:promptDefault');
            return prompts.find((prompt) => prompt._id === value)?.name
              ?? t('questionsAndAnswers:promptDefault');
          }}
          style={{ color: textColor, fontSize: '0.75rem' }}
          inputProps={{ 'aria-label': t('questionsAndAnswers:promptSelector') }}
        >
          <MenuItem value="">
            {t('questionsAndAnswers:promptDefault')}
          </MenuItem>
          {prompts.map((prompt) => {
            const summary = getDescriptionSummary(prompt.english?.description);
            return (
              <MenuItem key={prompt._id} value={prompt._id} style={{ whiteSpace: 'normal' }}>
                <div>
                  <Typography variant="body2">{prompt.name}</Typography>
                  {summary !== '' && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      {summary}
                    </Typography>
                  )}
                </div>
              </MenuItem>
            );
          })}
        </Select>
        {statusMessageKey && (
          <FormHelperText style={{ color: textColor, opacity: 0.7 }}>
            {t(statusMessageKey)}
          </FormHelperText>
        )}
      </FormControl>
      <PromptChangeConfirmModal
        isOpen={pendingPromptId !== null}
        onConfirm={handleConfirm}
        onClose={handleClose}
        description={pendingPromptDescription}
        promptName={pendingPrompt?.name}
        showHistoryWarning={hasMessages}
      />
    </>
  );
};
